export default function ProjectTypesElPaso() {
  const projectTypes = [
    {
      name: 'Office Buildings',
      popularity: 'High Demand',
      timeline: '12-24 months',
      image: 'https://public.readdy.ai/ai/img_res/21b4a90ac7ed90bba726ab02c6b2ceed.jpg',
      pros: ['Strong rental market', 'Long-term tenants', 'Professional environment', 'High property values', 'Stable income potential'],
      cons: ['Higher construction costs', 'Complex systems required', 'Longer project timelines'],
      description: 'Office building construction dominates El Paso\'s commercial market, serving the region\'s growing professional services sector. Modern offices feature energy-efficient systems, flexible floor plans, and technology infrastructure suited to contemporary workplace needs.'
    },
    {
      name: 'Retail Centers',
      popularity: 'Very Common',
      timeline: '8-16 months',
      image: 'https://readdy.ai/api/search-image?query=Contemporary%20retail%20shopping%20center%20construction%20in%20El%20Paso%2C%20modern%20commercial%20plaza%20with%20storefronts%20and%20parking%20area%2C%20southwestern%20architectural%20style%20with%20attractive%20facade%2C%20construction%20crew%20working%20on%20commercial%20retail%20project%2C%20mountain%20views%2C%20professional%20photography%20showing%20quality%20retail%20development&width=600&height=400&seq=commercial-retail-071&orientation=landscape',
      pros: ['Multiple revenue streams', 'Flexible tenant mix', 'High visibility locations', 'Strong consumer market', 'Adaptable spaces'],
      cons: ['Tenant turnover risk', 'Parking requirements', 'Competitive market'],
      description: 'Retail construction serves El Paso\'s growing consumer market with shopping centers, strip malls, and standalone stores. Successful projects require strategic locations, attractive design, and flexible spaces that accommodate various retail concepts.'
    },
    {
      name: 'Industrial Warehouses',
      popularity: 'Growing Sector',
      timeline: '6-12 months',
      image: 'https://readdy.ai/api/search-image?query=Large%20industrial%20warehouse%20construction%20in%20El%20Paso%20Texas%2C%20modern%20distribution%20center%20with%20loading%20docks%20and%20high%20ceilings%2C%20steel%20frame%20structure%20under%20construction%2C%20commercial%20construction%20equipment%20and%20workers%2C%20desert%20landscape%2C%20professional%20photography%20showing%20industrial%20building%20development%20and%20logistics%20facility&width=600&height=400&seq=commercial-warehouse-072&orientation=landscape',
      pros: ['Lower construction costs', 'Simple building systems', 'Strong logistics demand', 'El Paso location advantage', 'Quick construction timeline'],
      cons: ['Requires large land parcels', 'Limited tenant pool', 'Lower rental rates per square foot'],
      description: 'Warehouse construction thrives in El Paso due to strategic location near Mexico and major transportation corridors. Modern facilities feature efficient layouts, advanced logistics systems, and sustainable design for distribution and manufacturing operations.'
    },
    {
      name: 'Medical Facilities',
      popularity: 'Specialized Market',
      timeline: '10-18 months',
      image: 'https://readdy.ai/api/search-image?query=Modern%20medical%20clinic%20facility%20construction%20in%20El%20Paso%2C%20contemporary%20healthcare%20building%20with%20professional%20exterior%20design%2C%20commercial%20construction%20site%20for%20medical%20office%2C%20clean%20architectural%20lines%2C%20mountain%20backdrop%2C%20professional%20photography%20showing%20quality%20medical%20facility%20development%20and%20healthcare%20construction&width=600&height=400&seq=commercial-medical-073&orientation=landscape',
      pros: ['High rental rates', 'Stable long-term tenants', 'Growing healthcare demand', 'Recession-resistant sector', 'Professional tenant base'],
      cons: ['Complex regulatory requirements', 'Specialized systems needed', 'Higher construction costs'],
      description: 'Medical facility construction requires specialized expertise in healthcare regulations, infection control, and complex building systems. El Paso\'s growing population and healthcare needs drive demand for clinics, dental offices, and specialty medical facilities.'
    }
  ];

  return (
    <section className="py-20 px-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Common Commercial Projects in El Paso
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Understanding popular commercial project types helps you plan your development. Each type offers unique opportunities suited to different investment strategies and market demands in El Paso.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {projectTypes.map((type, index) => (
            <div key={index} className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={type.image} 
                  alt={`${type.name} construction in El Paso`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{type.name}</h3>
                    <div className="flex gap-4 text-sm">
                      <span className="text-teal-600 font-medium">{type.popularity}</span>
                      <span className="text-gray-600">Timeline: {type.timeline}</span>
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-gray-700 mb-4 leading-relaxed">{type.description}</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <i className="ri-check-line text-green-600"></i>
                      Advantages
                    </h4>
                    <ul className="space-y-1">
                      {type.pros.map((pro, i) => (
                        <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                          <span className="text-green-600 mt-0.5">•</span>
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <i className="ri-information-line text-orange-600"></i>
                      Considerations
                    </h4>
                    <ul className="space-y-1">
                      {type.cons.map((con, i) => (
                        <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                          <span className="text-orange-600 mt-0.5">•</span>
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
